import { Path } from '@vimeo/iris/components';
import { Header, Link, Paragraph } from '@vimeo/iris/typography';

import GuideWrapper from 'src/_components/GuideWrapper';
import styles from 'src/_components/GuideWrapper/GuideWrapper.module.scss';
import irisMarkdown from 'src/_components/Markdown';
import Meta from 'src/_components/Meta';
import { ucwords } from 'src/_tools/helpers';
import { withVersion } from 'src/_tools/withVersion';

import getCapabilitiesText from './_getCapabilitiesText';

export default function Response({ version = '' }) {
  const schema = devsite?.reference?.schema;
  const schemaLabel = devsite?.reference?.schema_label;

  function getDataType(data) {
    const dataSchema = data.schema;

    if (typeof data._linked_ref !== 'undefined') {
      const refLink = (
        <Link
          variant="minimal"
          href={withVersion(`/api/reference/response/${data._linked_ref.ref}`)}
        >
          {data._linked_ref.name}
        </Link>
      );

      if (dataSchema.type === 'object') {
        return (
          <>
            {data?.nullable === true ? 'Nullable ' : ''}
            {refLink}
          </>
        );
      } else if (dataSchema.type === 'array') {
        return <Paragraph size="2">array[ {refLink} ]</Paragraph>;
      }
    }

    return `${data?.nullable === true ? 'Nullable ' : ''}${ucwords(dataSchema.type)}`;
  }

  /**
   * Add optional line breaks between property name separators to encourage browsers
   * to display long properties in a way that makes sense.
   * @param name
   * @returns string Unsafe html containing a property name.
   */
  function getNameContent(name) {
    return name.split('.').join('<wbr>.');
  }

  function getParameterContent(content) {
    // mill includes an extra 'option descriptions' when describing enums, it's not necessary to display here
    return (
      content
        .replace(/Option descriptions:/, '')
        // we often include duplicate information on scopes, so remove it where it's duplicated.
        .replace(/(\s?This data requires a bearer token with the `[a-z]+` scope\.){2}/, '$1')
    );
  }

  return (
    <>
      {version ? (
        <Meta
          title={`Vimeo API Version ${version} Response Reference: ${schemaLabel}`}
          description={`Get complete documentation for ${schemaLabel.toLowerCase()} responses in the Vimeo API Version ${version}.`}
        />
      ) : (
        <Meta
          title={`Vimeo API Response Reference: ${schemaLabel}`}
          description={`Get complete documentation for ${schemaLabel.toLowerCase()} responses in the Vimeo API.`}
        />
      )}
      <GuideWrapper
        breadcrumbs={
          <Path>
            <Path.Link href={'/'}>Home</Path.Link>
            <Path.Link href={'/apps'}>API</Path.Link>
            <Path.Link href={'/api/reference'}>API Reference</Path.Link>
            <Path.Link href={'/api/reference/responses'}>Responses</Path.Link>
            <Path.Current>{schemaLabel}</Path.Current>
          </Path>
        }
        contentStyle={styles.main_full_width}
        nav={{ page: 'api-reference' }}
        showContextualMenu={false}
      >
        <Header>{schemaLabel}</Header>
        <Paragraph size="2">
          The <code>{schemaLabel?.toLowerCase()}</code> representation consists of the following
          fields.
        </Paragraph>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Data type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {schema &&
              Object.keys(schema).map(function (name, i) {
                const data = schema[name];
                return (
                  <tr key={`response-data-${i}`}>
                    <td>
                      <code dangerouslySetInnerHTML={{ __html: getNameContent(name) }} />
                    </td>
                    <td>{getDataType(data)}</td>
                    <td style={{ wordBreak: 'break-word' }}>
                      {irisMarkdown(getParameterContent(data.description), {
                        allowLevelZeroParagraphs: true,
                        convertParagraphsToSpans: false,
                        allowNestedParagraphs: false,
                      })}
                      {data?.['x-devsite-vendor-tags'] &&
                        irisMarkdown(getCapabilitiesText(data?.['x-devsite-vendor-tags']))}
                      {data?.['x-devsite-visibility-private'] &&
                        irisMarkdown('This is a private field.')}
                      {data?.['x-devsite-non-default'] &&
                        irisMarkdown(
                          `You must request this with the [\`fields\` parameter](/api/common-formats#json-filter).`,
                        )}
                      {data?.['x-devsite-deprecated'] && irisMarkdown(`This field is deprecated.`)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </GuideWrapper>
    </>
  );
}
